/**
 * MeshComponent - encapsulates a generic THREE.Mesh for actors.
 * Creates mesh from geometry/material or accepts existing mesh instance.
 * Handles UV scaling and material loading.
 */

import * as THREE from 'three';


import { DISABLE_TEXTURE_FLIP } from '../../const.js';
import { FeatureFlags } from '../../const.js';
import { MaterialUtils } from '../../materials.js';
import { ClassRegistry, EngineClass } from '../../systems/ClassRegistry.js';
import { getLatest } from '../../utils/Async.js';
import { resourceManager } from '../../utils/ResourceManager.js';
import { property } from '../../utils/serialization/decorator.js';
import { isNode } from '../../utils/Utils.js';
import { PrimitiveComponent } from '../PrimitiveComponent.js';
import { type ComponentDescriptionOptions, SceneComponent } from '../SceneComponent.js';

import type { IDisposer } from '../../interfaces.js';
import type { GenericMaterialType } from '../../materials.js';
import type { SceneGraphDebugNode } from '../../utils/SceneGraphUtils.js';
import type { PrimitiveComponentOptions } from '../PrimitiveComponent.js';

/**
 * Options for configuring MeshComponent
 */
export interface MeshComponentOptions extends PrimitiveComponentOptions {
  geometry?: THREE.BufferGeometry;
  material?: GenericMaterialType;
  uvScaleTilesPerUnit?: number;
}

@EngineClass('MeshComponent')
export class MeshComponent extends PrimitiveComponent {
  private _mesh: THREE.Mesh;

  private _material: GenericMaterialType = new THREE.MeshStandardMaterial();

  @property()
  public uvScaleTilesPerUnit: number = 0;

  constructor() {
    super();

    (this as any).isMesh = FeatureFlags.COLLAPSE_MESH_COMPONENT;

    const mesh = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1));
    mesh.setTransient(true);
    if (typeof(this._material) !== 'string') {
      mesh.material = this._material;
    }
    this.bindObject3DProperties(mesh, SceneComponent.boundObject3DProperties);
    if (!FeatureFlags.COLLAPSE_MESH_COMPONENT) {
      this.add(mesh);
    }
    this._mesh = mesh;
    this.onGeometryUpdated();
  }

  public override initialize(options?: MeshComponentOptions): void {
    super.initialize(options);
    this.uvScaleTilesPerUnit = options?.uvScaleTilesPerUnit ?? this.uvScaleTilesPerUnit;
    this.material = options?.material ?? this.material;
    this.geometry = options?.geometry ?? this.geometry;
  }

  get mesh(): THREE.Mesh {
    return this._mesh;
  }

  public override dispose(disposer: IDisposer): void {
    super.dispose(disposer);
    if (FeatureFlags.COLLAPSE_MESH_COMPONENT) {
      // when clapsed, mesh is not part of the scene graph, so we need to dispose it manually
      disposer.disposeNode(this._mesh);
    }
  }

  @property()
  get geometry(): THREE.BufferGeometry {
    return this._mesh.geometry;
  }

  set geometry(value: THREE.BufferGeometry) {
    if (this._mesh.geometry === value) return;
    this._mesh.geometry = value;
    this.onGeometryUpdated();
  }

  @property({ migrateFrom: 'meshMaterial' })
  get material(): GenericMaterialType {
    if (FeatureFlags.COLLAPSE_MESH_COMPONENT && SceneComponent.isRenderingScene) {
      return this._mesh.material;
    }
    return this._material;
  }

  set material(value: GenericMaterialType) {
    if (this._material === value) return;
    this._material = value;
    if (!isNode()) {
      // do not use assignLatest since this._meshMaterial records the original material, not the loaded one
      getLatest(
        this,
        '_material',
        () => resourceManager.loadGenericMaterial(value))
        .then((material) => {
          if (!material) {
            return;
          }
          this._mesh.material = material;
        });
    }
  }

  public override generateDebugNode(): SceneGraphDebugNode | null {
    const node = super.generateDebugNode()!;
    node.data = {
      ...node.data,
      geometry: ClassRegistry.getClassOrConstructorName(this._mesh.geometry),
      material: ClassRegistry.getClassOrConstructorName(this._mesh.material),
    };
    return node;
  }

  public override calcBoundingBox(): void {
    const mesh = this._mesh;
    mesh.geometry.computeBoundingBox();

    this._boundingBox.copy(mesh.geometry.boundingBox!).applyMatrix4(mesh.matrixWorld);
  }

  public override describe(options?: ComponentDescriptionOptions): Record<string, any> {
    const result = super.describe(options);
    if (options?.includeDetails) {
      result.meshType = this._mesh.geometry.type;
    }
    return result;
  }

  private onGeometryUpdated(): void {
    this.refreshPhysics();
    if (this.uvScaleTilesPerUnit) {
      MaterialUtils.scaleUVsBySize(this._mesh, this.uvScaleTilesPerUnit);
    }
    this.fixMeshUV();
  }

  private fixMeshUV() {
    if (DISABLE_TEXTURE_FLIP) {
      const geometry = this._mesh.geometry;
      if (geometry.userData.uvFlipped) {
        return;
      }
      const uvAttribute = geometry.attributes.uv;
      if (uvAttribute) {
        for (let i = 0; i < uvAttribute.count; i++) {
          uvAttribute.setXY(i, uvAttribute.getX(i), 1 - uvAttribute.getY(i));
        }
        uvAttribute.needsUpdate = true;
      }
      geometry.userData.uvFlipped = true;
    }
  }

  // placeholder properties and APIs to mirror THREE.Mesh
  public morphTargetInfluences?: number[] | undefined;
  public morphTargetDictionary?: { [key: string]: number } | undefined;

  public updateMorphTargets(): void {
    this._mesh.updateMorphTargets();
  }

  public getVertexPosition(index: number, target: THREE.Vector3): THREE.Vector3 {
    return this._mesh.getVertexPosition(index, target);
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Mesh';
  }
}

